import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import '@/styles/charts.css';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  TooltipProps,
} from 'recharts';
// Import the necessary type for the tooltip payload
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

interface TaskCompletionData {
  name: string;
  completed: number;
  inProgress: number;
  pending: number;
}

const data: TaskCompletionData[] = [
  { name: 'Mon', completed: 4, inProgress: 2, pending: 1 },
  { name: 'Tue', completed: 3, inProgress: 3, pending: 2 },
  { name: 'Wed', completed: 7, inProgress: 1, pending: 1 },
  { name: 'Thu', completed: 5, inProgress: 2, pending: 3 },
  { name: 'Fri', completed: 8, inProgress: 1, pending: 0 },
  { name: 'Sat', completed: 2, inProgress: 4, pending: 1 },
  { name: 'Sun', completed: 3, inProgress: 2, pending: 2 },
];

// Custom color palette for chart bars
const colors = {
  completed: 'hsl(142 72% 29%)',     // Emerald green
  inProgress: 'hsl(48 96% 53%)',      // Bright yellow
  pending: 'hsl(220 9% 87%)'          // Light gray
};

// CSS classes for color indicators are defined in /src/styles/charts.css

const CustomXAxis = (props: any) => (
  <XAxis
    {...props}
    stroke="hsl(var(--muted-foreground))"
    fontSize={12}
    tickLine={false}
  />
);

const CustomYAxis = (props: any) => (
  <YAxis
    {...props}
    stroke="hsl(var(--muted-foreground))"
    fontSize={12}
    tickLine={false}
  />
);

export function TaskCompletionChart() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Task Completion Overview</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <CustomXAxis dataKey="name" />
              <CustomYAxis />
              <Tooltip
                content={({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
                  if (active && payload?.length) {
                    // Convert all values to numbers before summing
                    const total = payload.reduce((sum, entry) => {
                      // Handle different value types safely
                      const numValue = typeof entry.value === 'number' 
                        ? entry.value 
                        : (Array.isArray(entry.value) ? 0 : Number(entry.value) || 0);
                      return sum + numValue;
                    }, 0);
                    
                    return (
                      <div className="rounded-lg border bg-background p-3 shadow-sm">
                        <div className="text-sm font-medium">{label}</div>
                        <div className="mt-1 space-y-1">
                          {payload.map((entry) => (
                            <div
                              key={entry.name}
                              className="flex items-center gap-2 text-sm"
                            >
                              <div
                                className={`chart-color-indicator ${entry.dataKey === 'completed' ? 'color-completed' : entry.dataKey === 'inProgress' ? 'color-in-progress' : 'color-pending'}`}
                              />
                              <span className="text-muted-foreground">
                                {entry.name}: {entry.value} tasks
                              </span>
                            </div>
                          ))}
                          <div className="pt-1 border-t text-sm text-muted-foreground">
                            Total: {total} tasks
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Legend />
              <Bar
                dataKey="completed"
                name="Completed"
                fill={colors.completed}
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="inProgress"
                name="In Progress"
                fill={colors.inProgress}
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="pending"
                name="Pending"
                fill={colors.pending}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}